import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { MoneyAmount } from "../lib/formatters";

// M3X.COMP F.17 / F.18 / F.19 — merit cycle detail with entries
// table + state-machine controls + budget envelope tracker.
export function CompCycleDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const cycleQ = useQuery({
    queryKey: ["comp-cycle", id],
    queryFn: () => api.getCompCycle(id!),
    enabled: !!id,
  });
  const entriesQ = useQuery({
    queryKey: ["comp-cycle-entries", id],
    queryFn: () => api.listCompCycleEntries(id!),
    enabled: !!id,
  });
  const budgetQ = useQuery({
    queryKey: ["comp-cycle-budget", id],
    queryFn: () => api.getCompCycleBudget(id!),
    enabled: !!id,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["comp-cycle", id] });
    queryClient.invalidateQueries({ queryKey: ["comp-cycle-entries", id] });
    queryClient.invalidateQueries({ queryKey: ["comp-cycle-budget", id] });
  };

  const setMultiplier = useMutation({
    mutationFn: (args: { employeeId: string; multiplier: string }) =>
      api.setCompCycleMultiplier(id!, args.employeeId, args.multiplier),
    onSuccess: refresh,
  });

  const runAction = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      refresh();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  if (cycleQ.isPending) return <Loading />;
  if (cycleQ.isError)
    return <ErrorPanel message={(cycleQ.error as Error).message} />;

  const c = cycleQ.data!;
  const entries = entriesQ.data ?? [];
  const budget = budgetQ.data ?? {};

  return (
    <div className="space-y-4">
      <PageHeader
        title={c.code}
        subtitle={`${c.name_en} — ${c.workflow_state}`}
      />

      {/* Budget envelope tracker — PR 26 */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">
            {t("comp_cycles.budget_envelope_pct")}
          </div>
          <div className="mt-1 text-base">{c.budget_envelope_pct}%</div>
        </div>
        <div
          className={`rounded border p-3 ${
            budget.over_budget
              ? "border-rose-300 bg-rose-50"
              : "border-emerald-200 bg-emerald-50"
          }`}
        >
          <div className="text-xs text-slate-500">
            {t("comp_cycles.budget_used")}
          </div>
          <div className="mt-1 text-base">{String(budget.pct_used ?? "—")}%</div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">
            {t("comp_cycles.pre_cycle_annual")}
          </div>
          <div className="mt-1 text-base">{c.pre_cycle_annual_base}</div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-500">
            {t("comp_cycles.modeled_annual")}
          </div>
          <div className="mt-1 text-base">{c.modeled_annual_base}</div>
        </div>
      </div>

      {/* Sonnet narrative — PR 25 */}
      {c.sonnet_narrative_en && (
        <div className="rounded border border-violet-200 bg-violet-50 p-3 text-sm">
          <div className="text-xs font-medium text-violet-900">
            ✦ {t("comp_cycles.narrative")}
          </div>
          <div className="mt-1">{c.sonnet_narrative_en}</div>
        </div>
      )}

      {/* State-machine controls */}
      <div className="flex flex-wrap gap-2">
        {(c.workflow_state === "planning_started" ||
          c.workflow_state === "ratings_collected" ||
          c.workflow_state === "modeled") && (
          <button
            type="button"
            onClick={() => runAction(() => api.modelCompCycle(id!))}
            className="rounded bg-brand-deep px-3 py-1.5 text-xs text-white hover:bg-slate-900"
          >
            {t("comp_cycles.model")}
          </button>
        )}
        {(c.workflow_state === "modeled" ||
          c.workflow_state === "ratings_collected") && (
          <button
            type="button"
            onClick={() => runAction(() => api.narrateCompCycle(id!))}
            className="rounded border border-violet-300 bg-white px-3 py-1.5 text-xs hover:bg-violet-50"
          >
            ✦ {t("comp_cycles.narrate")}
          </button>
        )}
        {c.workflow_state === "modeled" && (
          <button
            type="button"
            onClick={() => runAction(() => api.approveCompCycle(id!))}
            className="rounded bg-emerald-700 px-3 py-1.5 text-xs text-white hover:bg-emerald-700"
          >
            {t("comp_cycles.approve")}
          </button>
        )}
        {c.workflow_state === "approved" && (
          <button
            type="button"
            onClick={() => runAction(() => api.flipEffectiveCompCycle(id!))}
            className="rounded bg-emerald-700 px-3 py-1.5 text-xs text-white hover:bg-emerald-700"
          >
            {t("comp_cycles.flip_effective")}
          </button>
        )}
        {c.workflow_state === "effective_date_arrived" && (
          <button
            type="button"
            onClick={() => runAction(() => api.postCompCycle(id!))}
            className="rounded bg-emerald-700 px-3 py-1.5 text-xs text-white hover:bg-emerald-800"
          >
            {t("comp_cycles.post")}
          </button>
        )}
        {c.workflow_state !== "posted" && c.workflow_state !== "cancelled" && (
          <button
            type="button"
            onClick={() => runAction(() => api.cancelCompCycle(id!))}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
          >
            {t("comp_cycles.cancel")}
          </button>
        )}
      </div>

      {/* Per-employee entries with multiplier input — PR 24 */}
      <div>
        <div className="mb-2 text-xs font-medium text-slate-700">
          {t("comp_cycles.entries", { count: entries.length })}
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500">
              <th>{t("comp_cycles.employee")}</th>
              <th>{t("comp_cycles.pre_basic")}</th>
              <th>{t("comp_cycles.perf_multiplier")}</th>
              <th>{t("comp_cycles.proposed_basic")}</th>
              <th>{t("comp_cycles.delta_pct")}</th>
              <th>{t("comp_cycles.flags")}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="font-mono">{e.employee_id.slice(0, 8)}…</td>
                <td><MoneyAmount amount={e.pre_cycle_basic_salary} /></td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={3}
                    defaultValue={e.performance_multiplier}
                    onBlur={(ev) => {
                      const next = ev.target.value;
                      if (next !== e.performance_multiplier) {
                        setMultiplier.mutate({
                          employeeId: e.employee_id,
                          multiplier: next,
                        });
                      }
                    }}
                    className="w-20 rounded border border-slate-300 px-1 py-0.5 text-xs"
                  />
                </td>
                <td><MoneyAmount amount={e.proposed_basic_salary} /></td>
                <td
                  className={
                    e.delta_pct &&
                    Number(e.delta_pct) < 0
                      ? "text-rose-700"
                      : "text-emerald-700"
                  }
                >
                  {e.delta_pct ?? "—"}%
                </td>
                <td>
                  {e.below_band && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs">
                      below-band
                    </span>
                  )}
                  {e.above_cap && (
                    <span className="ml-1 rounded bg-rose-100 px-1.5 py-0.5 text-xs">
                      above-cap
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Link
        to="/operations/comp-cycle"
        className="text-xs text-slate-600 hover:underline"
      >
        ← {t("common.back")}
      </Link>
    </div>
  );
}
